/**
 * Wrapper around fetch to include the Rails csrf token,
 * parse JSON, and standardize / re-throw errors.
 *
 * @param {string} url      - the request url
 * @param {object} params   - the fetch params
 * @returns {Promise<any>}  - resolves with parsed JSON
 * @throws {Error}          - network or HTTP error
 */
export default async function LcFetch(url, params = {}) {
  // grab CSRF token
  const tokenEl = document.querySelector("[name='csrf-token']");
  const csrfToken = tokenEl ? tokenEl.content : "";

  // build headers + defaults
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
    "X-CSRF-Token": csrfToken,
    ...(params.headers || {})
  };

  const fetchParams = {
    ...params,
    headers
  };

  let res;
  try {
    res = await fetch(url, fetchParams);
  } catch (networkErr) {
    // network / CORS / DNS failures end up here
    const err = new Error(networkErr.message || "Network request failed");
    err.name = "FetchNetworkError";

    window.Rollbar?.error?.("LcFetch: Network Error", { err, fetchParams });
    throw err;
  }

  if (!res.ok && res.status >= 500) {
    const clone = res.clone(); // clone so we don’t consume the original body
    const body = await clone.json();

    const err = new Error(
      (body && body.message) || res.statusText || `HTTP ${res.status}`
    );
    err.name = "FetchHttpError";
    err.status = res.status;
    err.body = body;

    window.Rollbar?.error?.(`LcFetch: ${res.status} Error`, {
      err,
      fetchParams
    });
    throw err;
  }

  if (res.status >= 400) {
    const clone = res.clone(); // clone so we don’t consume the original body
    const body = await clone.json();
    window.Rollbar?.warn?.(`LcFetch: ${res.status} response`, {
      body,
      fetchParams
    });
  }

  return res;
}
